import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@swyftx/aviary/atoms/Label';
import { Select, SelectItemData } from '@swyftx/aviary/atoms/Select';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { DropdownSearch } from '@swyftx/aviary/molecules/DropdownSearch';

import { EmploymentStatus, EmploymentIndustry, ExpectedAnnualIncome } from '@shared/enums';

import { useOnboardingSelector, useOnboardingService } from '@Onboarding/Onboarding.context';
import { VerificationData } from '@Onboarding/types/Onboarding.types';

import { Controller, useForm } from 'react-hook-form';

import { OnboardingStepContainer } from './OnboardingStepContainer';

type FormInputs = Pick<VerificationData, 'employmentStatus' | 'employmentIndustry' | 'expectedAnnualIncome'>;

export const OccupationIncomeStep: React.FC = () => {
  const { t } = useTranslation('onboarding');
  const onboardingService = useOnboardingService();
  const verificationData = useOnboardingSelector((state) => state.context.verificationData);

  const {
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: {
      employmentStatus: verificationData?.employmentStatus,
      employmentIndustry: verificationData?.employmentIndustry,
      expectedAnnualIncome: verificationData?.expectedAnnualIncome,
    },
  });

  const employmentStatusList: SelectItemData[] = useMemo(
    () =>
      Object.values(EmploymentStatus).map((source) => ({
        label: t(`fields.employmentStatus.options.${source}`),
        value: source,
      })),
    [t],
  );

  const employmentIndustryList: SelectItemData[] = useMemo(
    () =>
      Object.values(EmploymentIndustry).map((industry) => ({
        label: t(`fields.employmentIndustry.options.${industry}`),
        value: industry,
      })),
    [t],
  );

  const expectedAnnualIncomeList: SelectItemData[] = useMemo(
    () =>
      Object.values(ExpectedAnnualIncome).map((income) => ({
        label: t(`fields.expectedAnnualIncome.options.${income}`),
        value: income,
      })),
    [t],
  );

  const selectedEmploymentStatus = watch('employmentStatus');

  const shouldShowIndustry = useMemo(
    () =>
      selectedEmploymentStatus &&
      ![EmploymentStatus.STUDENT, EmploymentStatus.RETIRED, EmploymentStatus.NOT_EMPLOYED].includes(
        selectedEmploymentStatus,
      ),
    [selectedEmploymentStatus],
  );

  const onSubmit = (data: FormInputs) => {
    onboardingService.send({ type: 'NEXT', data });
  };

  return (
    <OnboardingStepContainer onSubmit={handleSubmit(onSubmit)} submitDisabled={!isValid}>
      <main className='grid gap-32'>
        <Body size='medium' color='secondary' className='mb-32'>
          {t('steps.setupProfile.occupationIncome.description')}
        </Body>

        <Controller
          name='employmentStatus'
          control={control}
          rules={{ required: t('fields.employmentStatus.required') }}
          render={({ field }) => (
            <section className='grid gap-8'>
              <Label>{t('fields.employmentStatus.label')}</Label>
              <Select
                placeholder='Select'
                items={employmentStatusList}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);

                  if (!shouldShowIndustry) {
                    setValue('employmentIndustry', undefined, { shouldValidate: true });
                  }
                }}
                error={!!errors.employmentStatus?.message}
                required
              />
            </section>
          )}
        />

        {shouldShowIndustry && (
          <Controller
            name='employmentIndustry'
            control={control}
            rules={{ required: t('fields.employmentIndustry.required') }}
            render={({ field }) => (
              <section className='grid gap-8'>
                <DropdownSearch
                  errorMessage={errors.employmentIndustry?.message}
                  items={employmentIndustryList}
                  label={t('fields.employmentIndustry.label')}
                  modalTitle={t('fields.employmentIndustry.modalTitle')}
                  onValueChange={field.onChange}
                  placeholder={t('fields.employmentIndustry.placeholder')}
                  value={field.value}
                />
              </section>
            )}
          />
        )}

        {selectedEmploymentStatus && (
          <Controller
            name='expectedAnnualIncome'
            control={control}
            rules={{
              required: t('fields.expectedAnnualIncome.required'),
            }}
            render={({ field }) => (
              <section className='grid gap-8'>
                <Label>{t('fields.expectedAnnualIncome.label')}</Label>
                <Select
                  placeholder='Select'
                  items={expectedAnnualIncomeList}
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                  }}
                  error={!!errors.expectedAnnualIncome?.message}
                  required
                />
              </section>
            )}
          />
        )}
      </main>
    </OnboardingStepContainer>
  );
};
